<template>
  <div>
    <report-panel :anchor="'#finances'" v-if="report.companyoverview.financials.length > 0">
      <div slot="title">Key Financials</div>
      <div slot="body">
        <!-- panel content start -->
        <table class="table table-striped" id="key-financials-table">
          <tbody>
            <tr>
              <header-cell>
                <template slot="heading">Latest Accounts:</template>
              </header-cell>
              <simple-cell
                :display-date="formatDate(report.companyoverview.created_at)"
                :display-value="formatDate(report.companyoverview.latest_accounts_date)"
                :show-diff="showDiff"
                :has-diff="hasAccountsDiff"
                :other-date="accountsDiff.date"
                :center="true"
                :other-value="formatDate(accountsDiff.latest_accounts_date)"
              />
            </tr>
            <tr>
              <header-cell>
                <template slot="heading">Turnover:</template>
              </header-cell>
              <standard-cell
                :currency-date="formatDate(report.companyoverview.created_at)"
                :currency-symbol="report.companyoverview.currency.html_symbol"
                :currency-value="report.companyoverview.turnover"
                :current-date="turnoverDiff.date"
                :current-value="turnoverDiff.turnover"
                :has-diff="hasTurnoverDiff"
                :percentage-value="report.companyoverview.turnover_delta_percentage"
                :direction="report.companyoverview.turnover_delta_direction"
                :is-credit-safe-report="report.is_creditsafe_report"
                :show-diff="showDiff"
                :show-percentage="true"
              />
            </tr>
            <tr>
              <header-cell>
                <template slot="heading">Gross Profit:</template>
              </header-cell>
              <standard-cell
                :currencySymbol="report.companyoverview.currency.html_symbol"
                :currencyValue="report.companyoverview.gross_profit"
                :percentageValue="report.companyoverview.gross_profit_delta_percentage"
                :direction="report.companyoverview.gross_profit_delta_direction"
                :isCreditSafeReport="report.is_creditsafe_report"
                :showPercentage="true"
              ></standard-cell>
            </tr>
            <tr>
              <header-cell>
                <template slot="heading">Net Profit/(Loss) Before Tax:</template>
              </header-cell>
              <standard-cell
                :currencySymbol="report.companyoverview.currency.html_symbol"
                :currencyValue="report.companyoverview.net_profit_before_tax"
                :percentageValue="report.companyoverview.net_profit_before_tax_delta_percentage"
                :direction="report.companyoverview.net_profit_before_tax_delta_direction"
                :isCreditSafeReport="report.is_creditsafe_report"
                :showPercentage="true"
              ></standard-cell>
            </tr>
            <tr>
              <header-cell>
                <template slot="heading">Total Fixed Assets:</template>
              </header-cell>
              <standard-cell
                :currencySymbol="report.companyoverview.currency.html_symbol"
                :currencyValue="report.companyoverview.total_fixed_assets"
                :percentageValue="report.companyoverview.total_fixed_assets_delta_percentage"
                :direction="report.companyoverview.total_fixed_assets_delta_direction"
                :isCreditSafeReport="report.is_creditsafe_report"
                :showPercentage="true"
              ></standard-cell>
            </tr>
            <tr>
              <header-cell>
                <template slot="heading">Stock:</template>
              </header-cell>
              <standard-cell
                :currencySymbol="report.companyoverview.currency.html_symbol"
                :currencyValue="report.companyoverview.stock"
                :percentageValue="report.companyoverview.stock_delta_percentage"
                :direction="report.companyoverview.stock_delta_direction"
                :isCreditSafeReport="report.is_creditsafe_report"
                :showPercentage="true"
              ></standard-cell>
            </tr>
            <tr>
              <header-cell>
                <template slot="heading">Cash:</template>
              </header-cell>
              <standard-cell
                :currencySymbol="report.companyoverview.currency.html_symbol"
                :currencyValue="report.companyoverview.cash"
                :percentageValue="report.companyoverview.cash_delta_percentage"
                :direction="report.companyoverview.cash_delta_direction"
                :isCreditSafeReport="report.is_creditsafe_report"
                :showPercentage="true"
              ></standard-cell>
            </tr>
            <tr>
              <header-cell>
                <template slot="heading">Employee Numbers:</template>
              </header-cell>
              <simple-cell
                :display-date="formatDate(report.companyoverview.created_at)"
                :display-value="(report.companyoverview.employee_numbers === null ? '-' : Math.trunc(report.companyoverview.employee_numbers))"
                :has-diff="hasEmployeeNumbersDiff"
                :show-diff="showDiff"
                :other-date="employeeNumbersDiff.date"
                :other-value="employeeNumbersDiff.employee_numbers"
                :percentage-value="report.companyoverview.employee_numbers_delta_percentage"
                :direction="report.companyoverview.employee_numbers_delta_direction"
                :is-credit-safe-report="report.is_creditsafe_report"
              >
              </simple-cell>
            </tr>
            <tr>
              <header-cell>
                <template slot="heading">Wages and Salaries:</template>
              </header-cell>
              <standard-cell
                :currencySymbol="report.companyoverview.currency.html_symbol"
                :currencyValue="report.companyoverview.wages_salaries"
                :percentageValue="report.companyoverview.wages_salaries_delta_percentage"
                :direction="report.companyoverview.wages_salaries_delta_direction"
                :isCreditSafeReport="report.is_creditsafe_report"
                :showPercentage="true"
              ></standard-cell>
            </tr>
          </tbody>
        </table>
      </div>
    </report-panel>
  </div>
</template>

<script>
import toolsMixin from '@/components/reports/ToolsMixin';
import ReportPanel from '@/components/reports/components/ReportPanel';
import HeaderCell from '@/components/reports/panels/financials/tablecells/HeaderCell';
import StandardCell from '@/components/reports/panels/financials/tablecells/StandardCell';
import SimpleCell from '@/components/reports/panels/financials/tablecells/SimpleCell';

export default {
  components: {
    ReportPanel,
    HeaderCell,
    StandardCell,
    SimpleCell,
  },
  computed: {
    accountsDiff() {
      let result = this.diffData.find((d) => d.type === 'accounts');
      if (result === undefined) {
        result = {
          date: 'N/A',
          latest_accounts_date: 'N/A',
        };
      }

      return result;
    },
    employeeNumbersDiff() {
      let result = this.diffData.find((d) => d.type === 'employee_numbers');
      if (result === undefined) {
        result = {
          date: 'N/A',
          employee_numbers: 'N/A',
        };
      }

      return result;
    },
    turnoverDiff() {
      let result = this.diffData.find((d) => d.type === 'turnover');
      if (result === undefined) {
        result = {
          date: 'N/A',
          turnover: 'N/A',
        };
      }

      return result;
    },
    hasAccountsDiff() {
      const result = this.diffData.find((d) => d.type === 'accounts');
      return result !== undefined;
    },
    hasEmployeeNumbersDiff() {
      const result = this.diffData.find((d) => d.type === 'employee_numbers');
      return result !== undefined;
    },
    hasTurnoverDiff() {
      const result = this.diffData.find((d) => d.type === 'turnover');
      return result !== undefined;
    },
  },
  mixins: [toolsMixin],
  props: ['diffData', 'report', 'showDiff'],
};
</script>
